/**
 * Utility function for querySelector
 * @param {string} selector - The CSS selector
 * @param {Document|Element} [context=document] - The context to search within
 * @returns {Element|null}
 */
const qs = (selector, context = document) => context.querySelector(selector);

/**
 * Utility function for querySelectorAll
 * @param {string} selector - The CSS selector
 * @param {Document|Element} [context=document] - The context to search within
 * @returns {NodeListOf<Element>}
 */
const qsa = (selector, context = document) => context.querySelectorAll(selector);

/**
 * Initializes the mobile menu toggle functionality.
 */
function initMobileMenu() {
  const menuButton = qs('#mobile-menu-button');
  const mobileMenu = qs('#mobile-menu');

  if (!menuButton || !mobileMenu) {
    console.warn('Mobile menu button or menu element not found.');
    return;
  }

  menuButton.addEventListener('click', () => {
    mobileMenu.classList.toggle('hidden');
    const isExpanded = !mobileMenu.classList.contains('hidden');
    menuButton.setAttribute('aria-expanded', isExpanded.toString());
  });

  // Close menu when a link inside it is clicked
  qsa('a', mobileMenu).forEach(link => {
    link.addEventListener('click', () => {
      mobileMenu.classList.add('hidden');
      menuButton.setAttribute('aria-expanded', 'false');
    });
  });
}

/**
 * Initializes smooth scrolling for anchor links.
 */
function initSmoothScroll() {
  qsa('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const targetId = this.getAttribute('href');
      // Ensure it's a valid ID selector and not just "#"
      if (targetId.length <= 1) return;

      try {
        const targetElement = qs(targetId);
        if (targetElement) {
          e.preventDefault();
          const header = qs('header');
          const headerOffset = header ? header.offsetHeight : 0;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      } catch (error) {
        console.error(`Error finding or scrolling to element with ID "${targetId}":`, error);
      }
    });
  });
}

/**
 * Manages the FAQ accordion within product image containers.
 */
function initProductFAQAccordion() {
  // Disable FAQ functionality on mobile devices (screen width < 768px)
  if (window.innerWidth < 768) {
    return;
  }

  const imageContainers = qsa('#products .flex-1.overflow-hidden');

  if (imageContainers.length === 0) {
    // No product containers found, nothing to initialize.
    return;
  }

  // --- Accordion Helper Functions ---

  const closeAccordionItem = (button, answer) => {
    if (!button || !answer) return;
    const plusIcon = qs('.plus-icon', button);
    const minusIcon = qs('.minus-icon', button);

    button.setAttribute('aria-expanded', 'false');
    plusIcon?.classList.remove('hidden');
    minusIcon?.classList.add('hidden');

    // Start closing animation
    answer.style.maxHeight = answer.scrollHeight + 'px';
    requestAnimationFrame(() => { // Ensure the height is set before transitioning to 0
        requestAnimationFrame(() => { // Ensure the first frame is painted before setting to 0
            answer.style.maxHeight = '0px';
        });
    });


    // Cleanup after transition
    answer.addEventListener('transitionend', function cleanup() {
      if (button.getAttribute('aria-expanded') === 'false') {
        answer.classList.add('max-h-0'); // Add class for state consistency
        answer.style.maxHeight = null; // Remove inline style
      }
      answer.removeEventListener('transitionend', cleanup); // Explicitly remove listener
    });
  };

  const openAccordionItem = (button, answer) => {
    if (!button || !answer) return;
    const plusIcon = qs('.plus-icon', button);
    const minusIcon = qs('.minus-icon', button);

    button.setAttribute('aria-expanded', 'true');
    plusIcon?.classList.add('hidden');
    minusIcon?.classList.remove('hidden');

    // Prepare for opening animation
    answer.classList.remove('max-h-0'); // Remove class restricting height
    answer.style.maxHeight = '0px'; // Set to 0 initially for transition effect

    requestAnimationFrame(() => { // Ensure 0px is applied before calculating scrollHeight
        const scrollHeight = answer.scrollHeight;
        answer.style.maxHeight = scrollHeight + 'px'; // Set target height
    });


    // Cleanup after transition
    answer.addEventListener('transitionend', function cleanup() {
      if (button.getAttribute('aria-expanded') === 'true') {
        answer.style.maxHeight = null; // Remove inline style for dynamic content
      }
       answer.removeEventListener('transitionend', cleanup); // Explicitly remove listener
    });
  };

  // Function to handle clicks on accordion buttons within a specific container
  const handleAccordionClick = (event) => {
    const button = event.currentTarget; // The button that was clicked
    const answer = button.closest('dt')?.nextElementSibling; // Find the <dd>

    if (!answer || answer.tagName !== 'DD') {
        console.warn('Could not find answer element for accordion button:', button);
        return;
    }

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const faqContainer = button.closest('.faq-content'); // Find the parent FAQ container

    if (isExpanded) {
      closeAccordionItem(button, answer);
    } else {
      // Close all other open items *within the same container* first
      if (faqContainer) {
          qsa('dt button[aria-expanded="true"]', faqContainer).forEach(otherButton => {
              if (otherButton !== button) {
                  const otherAnswer = otherButton.closest('dt')?.nextElementSibling;
                  if (otherAnswer) {
                      closeAccordionItem(otherButton, otherAnswer);
                  }
              }
          });
      }
      // Then open the clicked item
      openAccordionItem(button, answer);
    }
  };

  // Function to initialize accordion listeners for a specific FAQ container
  // This ensures listeners are added only once.
  const setupAccordionListeners = (faqContainer) => {
    if (!faqContainer || faqContainer.dataset.accordionInitialized === 'true') {
        return; // Already initialized or no container
    }
    const faqButtons = qsa('dt button', faqContainer);
    faqButtons.forEach(button => {
      // Remove potential old listener before adding (safer than cloning)
      // Note: This assumes handleAccordionClick is the *only* click listener.
      // If other click listeners exist, a more robust method is needed.
      // For this specific case, it's likely fine.
      button.removeEventListener('click', handleAccordionClick);
      button.addEventListener('click', handleAccordionClick);
    });
    faqContainer.dataset.accordionInitialized = 'true'; // Mark as initialized
  };

  // Function to reset all accordions within a specific FAQ container to closed state
  const resetAccordion = (faqContainer) => {
    if (!faqContainer) return;
    qsa('dt button[aria-expanded="true"]', faqContainer).forEach(button => {
      const answer = button.closest('dt')?.nextElementSibling;
      if (answer) {
        const plusIcon = qs('.plus-icon', button);
        const minusIcon = qs('.minus-icon', button);

        button.setAttribute('aria-expanded', 'false');
        plusIcon?.classList.remove('hidden');
        minusIcon?.classList.add('hidden');
        answer.style.maxHeight = null; // Remove inline style from potential animations
        answer.classList.add('max-h-0'); // Ensure it's visually collapsed using the class
      }
    });
     // Optional: Mark as needing re-initialization if listeners were removed during reset
     // delete faqContainer.dataset.accordionInitialized;
  };


  // --- Image Container Interaction Logic ---

  imageContainers.forEach(container => {
    const faqContent = qs('.faq-content', container);
    if (!faqContent) return; // Skip if no FAQ content in this container

    // Use transitionend to detect when the container finishes expanding/shrinking
    container.addEventListener('transitionend', (event) => {
      // Only act on the flex-grow/flex-basis transition
      if (event.propertyName.includes('flex') && faqContent) {
        const computedStyle = window.getComputedStyle(container);
        // Check flex-grow as it's likely the property being transitioned for expansion
        const isExpanded = parseFloat(computedStyle.flexGrow) > 1;

        if (isExpanded) {
          // Fade in FAQ content
          faqContent.classList.remove('opacity-0', 'pointer-events-none');
          faqContent.classList.add('opacity-100');
          setupAccordionListeners(faqContent); // Initialize accordion when shown
        } else {
          // Fade out FAQ content
          faqContent.classList.remove('opacity-100');
          faqContent.classList.add('opacity-0', 'pointer-events-none');
          resetAccordion(faqContent); // Reset accordion state when hidden
        }
      }
    });

    // Also handle mouseleave as a fallback or primary trigger for closing
    container.addEventListener('mouseleave', () => {
       // Only act if the FAQ is currently visible (opacity-100)
       if (faqContent && faqContent.classList.contains('opacity-100')) {
          // Fade out
          faqContent.classList.remove('opacity-100');
          faqContent.classList.add('opacity-0', 'pointer-events-none');
          resetAccordion(faqContent); // Reset accordion state
       }
    });
  });
}


// --- Initialization ---
// Wait for the DOM to be fully loaded before running scripts
document.addEventListener('DOMContentLoaded', () => {
  initMobileMenu();
  initSmoothScroll();
  initProductFAQAccordion();
  
  // Re-initialize FAQ accordion on window resize (for device rotation)
  window.addEventListener('resize', () => {
    // Debounce resize events
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = setTimeout(() => {
      initProductFAQAccordion();
    }, 250);
  });
});